import _ from 'lodash'
import { withCompPreviewInfo } from '@wix/editor-elements-integrations'
import { getTextPresets, ooiStyleParamsUtil, processOoiCss, isOOI, isRTL } from '@wix/thunderbolt-commons'
import { getColorValue, getSiteColors, getTpaFontParamValue, splitPropertiesValues } from './tpaStyleUtils'
import { getHeightInPixels, getWidthInPixels } from '@wix/editor-elements-common-utils'
import type { ColorMap, CompDefinition, StyleProperties, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import type { WidgetDataViewItem } from './TPAWidget'
import { getFontToWixThemeVariableMap } from '@wix/wix-site-theme-variables'

type TPAWidgetDefinition = CompDefinition<never, WidgetDataViewItem, never, never>

const FONT_TO_WIX_THEME_VARIABLE_MAP = getFontToWixThemeVariableMap()

const { shouldOverrideStyleParams, overrideStyleParams } = ooiStyleParamsUtil
const isPublicSegment = process.env.PACKAGE_NAME !== 'thunderbolt-ds'

const remapThemeColors = (themeColors: ColorMap, siteVariables: Record<string, string>) => {
	return themeColors.map((color, index) => siteVariables[`--color_${index}`] ?? color)
}

const remapSiteFonts = (siteFonts: ThemeFontMap, rawSiteVariables: Record<string, string>) => {
	return Object.entries(siteFonts).reduce((acc, [key, value]) => {
		const varName = FONT_TO_WIX_THEME_VARIABLE_MAP[key]
		if (varName && varName in rawSiteVariables) {
			const variableValue = rawSiteVariables[varName]?.replace('spx', 'px')
			acc.push(variableValue ?? value)
		} else {
			acc.push(value)
		}
		return acc
	}, [] as string[])
}

export const resolveStyles = ({ siteFonts: _siteFonts, themeColors: _themeColors, styleProperties, siteVariables, isBuilderComponentModel }: {
	siteFonts: ThemeFontMap
	themeColors: ColorMap
	styleProperties: StyleProperties
	siteVariables: { rawVariables?: { [key: string]: string }; tpaColors?: { [key: string]: string } }
	isBuilderComponentModel: boolean
}) => {
	const rawVariables = Object.entries(siteVariables?.rawVariables ?? {}).reduce((acc: { [key: string]: string }, [key, value]) => {
		const newKey = key.replace('--', '')
		acc[newKey] = value
		return acc
	}, {})

	const siteFonts = isBuilderComponentModel ? remapSiteFonts(_siteFonts, rawVariables) : _.values(_siteFonts)

	const {
		color: colorParams,
		number: numberParams,
		boolean: booleanParams,
		font: fontParams,
		string: stringParams,
	} = splitPropertiesValues(styleProperties)

	const themeColors = isBuilderComponentModel ? remapThemeColors(_themeColors, siteVariables?.tpaColors ?? {}) : _themeColors

	return {
		siteColors: getSiteColors(themeColors),
		siteTextPresets: getTextPresets(siteFonts),
		style: {
			colors: _.mapValues(colorParams, (color, name) =>
				getColorValue(color, styleProperties[`alpha-param_color_${name}`], themeColors)
			),
			numbers: _.mapValues(numberParams, Number),
			booleans: _.mapValues(booleanParams, (value) => value && value !== 'false'),
			fonts: _(fontParams)
				.mapValues((fontParam) => getTpaFontParamValue(fontParam, siteFonts))
				.pickBy((v) => !!v)
				.value(),
			strings: stringParams,
			googleFontsCssUrl: '',
			uploadFontFaces: '',
		},
	}
}

export const css = withCompPreviewInfo<object, TPAWidgetDefinition>()(
	[
		'compId',
		'compData',
		'styleProperties',
		// @ts-ignore
		'formFactor',
		'language',
		'siteColors',
		'siteFonts',
		'compLayout',
		'hasResponsiveLayout',
		// @ts-ignore
		'anywhereThemeOverride',
		'compSingleLayout',
		'isOneDocMigrated',
		// @ts-ignore
		'isResponsive',
		// @ts-ignore
		'siteVariables',
		// @ts-ignore
		'isBuilderComponentModel',
		...(process.env.PACKAGE_NAME === 'thunderbolt-ds'
			? (['componentViewMode', 'isLivePreviewOpenForEditor', 'isAdi'] as const)
			: []),
	],
	({
		compId,
		compData,
		styleProperties,
		// @ts-ignore
		formFactor,
		language,
		siteColors: themeColors,
		siteFonts,
		compLayout,
		hasResponsiveLayout,
		// @ts-ignore
		anywhereThemeOverride,
		componentViewMode,
		// @ts-ignore
		isLivePreviewOpenForEditor,
		// @ts-ignore
		isAdi,
		compSingleLayout,
		isOneDocMigrated,
		// @ts-ignore
		isResponsive,
		// @ts-ignore
		runAndReport,
		// @ts-ignore
		siteVariables,
		// @ts-ignore
		isBuilderComponentModel,
	}) => {
		const shouldRenderAsOoi = isOOI({
			widgetSpec: compData.widgetSpecData,
			isMigratingToOoi: compData.isMigratingToOoi,
			componentViewMode,
			isLivePreviewOpenForEditor,
			isAdi,
		})

		const disableNoCssBundle =
			process.env.PACKAGE_NAME === 'thunderbolt-ds' &&
			!!compData.referenceId &&
			['621bc837-5943-4c76-a7ce-a0e38185301f', 'cc882051-73c9-41a6-8f90-f6ebc9f10fe1'].includes(compData.widgetId)

		if (!shouldRenderAsOoi || !compData.cssConfig || disableNoCssBundle) {
			return {}
		}

		const styleData = resolveStyles({ siteFonts, themeColors, styleProperties, siteVariables, isBuilderComponentModel })

		const { widgetId, appSettings, tpaData } = compData
		if (shouldOverrideStyleParams(widgetId)) {
			styleData.style = overrideStyleParams({
				styleParams: styleData.style,
				widgetId,
				appSettings,
				tpaData,
				isResponsive: hasResponsiveLayout,
				anywhereThemeOverride,
			})
		}

		// TODO: use isClassicComponent api when this PR merged: https://github.com/wix-private/thunderbolt/pull/33237
		const isClassicComponent = !hasResponsiveLayout
		const getClassicComponentWidth = (_isOneDocMigrated: boolean) => {
			if (_isOneDocMigrated) {
				return getWidthInPixels(compSingleLayout) || ('' as const)
			}
			return compLayout.docked?.left ? ('' as const) : (compLayout.width as number)
		}

		const getClassicComponentHeight = (_isOneDocMigrated: boolean) => {
			if (_isOneDocMigrated) {
				return getHeightInPixels(compSingleLayout) || ('' as const)
			}
			return compLayout.height as number
		}

		const dimensions = isClassicComponent
			? { width: getClassicComponentWidth(isOneDocMigrated), height: getClassicComponentHeight(isOneDocMigrated) }
			: { width: '' as const, height: '' as const }

		const cssString = processOoiCss({
			styleData,
			prefixSelector: `.${compId}`,
			shouldRemoveStaticCss: isPublicSegment,
			isRTL: isRTL(language),
			isMobile: formFactor === 'mobile',
			dimensions,
			cssConfig: compData.cssConfig,
			usesCssPerBreakpoint: !!compData.usesCssPerBreakpoint,
			runAndReport: runAndReport || ((fn) => fn()),
			widgetId,
			tpaData,
			isResponsive,
			variables: siteVariables,
		})

		return cssString
	}
)
